import {
  Box,
  VStack,
  Text,
  Flex,
  Radio,
  RadioGroup,
  useColorModeValue,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';

import DriverDropZone from './DriverDropZone';
import DraggableAssignedDriverChip from './DraggableAssignedDriverChip';
import { getLearnerLabel } from '../../utils/driverAssignment';
import type {
  AssignmentVehicle,
  AssignmentDriver,
} from 'api/quote-marketplace';

interface VehicleAssignmentColumnProps {
  vehicle: AssignmentVehicle;
  drivers: AssignmentDriver[];
  quoteProvince: number;
  onRemoveDriver: (
    vehicleId: number,
    driverId: number,
    zone: 'principal' | 'secondary' | 'occasional' | 'learners',
  ) => void;
  onTelematicsChange: (vehicleId: number, enabled: boolean) => void;
}

const VehicleAssignmentColumn = ({
  vehicle,
  drivers,
  quoteProvince,
  onRemoveDriver,
  onTelematicsChange,
}: VehicleAssignmentColumnProps) => {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const headerBg = useColorModeValue('gray.50', 'navy.800');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const vehicleTitle =
    [vehicle.vehicle_model_year, vehicle.make, vehicle.model]
      .filter(Boolean)
      .join(' ') || `Vehicle ${vehicle.id}`;

  const getDriverName = (driverId: number): string => {
    const driver = drivers.find((d) => d.id === driverId);
    if (!driver) return `Driver ${driverId}`;
    return (
      `${driver.first_name} ${driver.last_name}`.trim() || `Driver ${driverId}`
    );
  };

  const isDriverExcluded = (driverId: number): boolean => {
    const driver = drivers.find((d) => d.id === driverId);
    return driver?.excluded ?? false;
  };

  const learnerSectionLabel = getLearnerLabel(quoteProvince);

  const principalDriver = vehicle.principal_driver_id
    ? drivers.find((d) => d.id === vehicle.principal_driver_id)
    : null;

  return (
    <Box
      minW="220px"
      maxW="240px"
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      overflow="hidden"
      flexShrink={0}
    >
      <Box
        bg={headerBg}
        p={3}
        borderBottomWidth="1px"
        borderColor={borderColor}
      >
        <Text fontWeight="600" fontSize="sm" noOfLines={1}>
          {vehicleTitle}
        </Text>
        {vehicle.vin && (
          <Text fontSize="xs" color={labelColor} noOfLines={1}>
            VIN: {vehicle.vin}
          </Text>
        )}
      </Box>

      <VStack spacing={3} p={3} align="stretch">
        <Box>
          <Text fontSize="xs" fontWeight="600" color={labelColor} mb={1}>
            Registered Owner
          </Text>
          <Text fontSize="sm">{vehicle.registered_owner_name || '-'}</Text>
        </Box>

        <Box>
          <Text fontSize="xs" fontWeight="600" color={labelColor} mb={1}>
            Principal Operator
          </Text>
          <DriverDropZone
            id={`principal-${vehicle.id}`}
            label="Drop driver here"
            isEmpty={!principalDriver}
          >
            {principalDriver && (
              <DraggableAssignedDriverChip
                driverId={principalDriver.id}
                name={getDriverName(principalDriver.id)}
                vehicleId={vehicle.id}
                zone="principal"
                onRemove={() =>
                  onRemoveDriver(vehicle.id, principalDriver.id, 'principal')
                }
                isExcluded={principalDriver.excluded}
              />
            )}
          </DriverDropZone>
        </Box>

        <Box>
          <Text fontSize="xs" fontWeight="600" color={labelColor} mb={1}>
            Secondary
          </Text>
          <DriverDropZone
            id={`secondary-${vehicle.id}`}
            label="Drop driver(s) here"
            isEmpty={vehicle.secondary_drivers.length === 0}
          >
            <Wrap spacing={1}>
              {vehicle.secondary_drivers.map((driverId) => (
                <WrapItem key={driverId}>
                  <DraggableAssignedDriverChip
                    driverId={driverId}
                    name={getDriverName(driverId)}
                    vehicleId={vehicle.id}
                    zone="secondary"
                    onRemove={() =>
                      onRemoveDriver(vehicle.id, driverId, 'secondary')
                    }
                    isExcluded={isDriverExcluded(driverId)}
                  />
                </WrapItem>
              ))}
            </Wrap>
          </DriverDropZone>
        </Box>

        <Box>
          <Text fontSize="xs" fontWeight="600" color={labelColor} mb={1}>
            Occasional
          </Text>
          <DriverDropZone
            id={`occasional-${vehicle.id}`}
            label="Drop driver(s) here"
            isEmpty={vehicle.occasional_drivers.length === 0}
          >
            <Wrap spacing={1}>
              {vehicle.occasional_drivers.map((driverId) => (
                <WrapItem key={driverId}>
                  <DraggableAssignedDriverChip
                    driverId={driverId}
                    name={getDriverName(driverId)}
                    vehicleId={vehicle.id}
                    zone="occasional"
                    onRemove={() =>
                      onRemoveDriver(vehicle.id, driverId, 'occasional')
                    }
                    isExcluded={isDriverExcluded(driverId)}
                  />
                </WrapItem>
              ))}
            </Wrap>
          </DriverDropZone>
        </Box>

        <Box>
          <Text fontSize="xs" fontWeight="600" color={labelColor} mb={1}>
            {learnerSectionLabel}
          </Text>
          <DriverDropZone
            id={`learners-${vehicle.id}`}
            label="Drop learner here"
            isEmpty={(vehicle.learner_drivers?.length ?? 0) === 0}
          >
            <Wrap spacing={1}>
              {(vehicle.learner_drivers ?? []).map((driverId) => (
                <WrapItem key={driverId}>
                  <DraggableAssignedDriverChip
                    driverId={driverId}
                    name={getDriverName(driverId)}
                    vehicleId={vehicle.id}
                    zone="learners"
                    onRemove={() =>
                      onRemoveDriver(vehicle.id, driverId, 'learners')
                    }
                    isExcluded={isDriverExcluded(driverId)}
                  />
                </WrapItem>
              ))}
            </Wrap>
          </DriverDropZone>
        </Box>

        <Box>
          <Text fontSize="xs" fontWeight="600" color={labelColor} mb={1}>
            Telematics
          </Text>
          <RadioGroup
            value={vehicle.telematics_enabled ? 'yes' : 'no'}
            onChange={(val) => onTelematicsChange(vehicle.id, val === 'yes')}
            size="sm"
          >
            <Flex gap={4}>
              <Radio value="yes">Yes</Radio>
              <Radio value="no">No</Radio>
            </Flex>
          </RadioGroup>
        </Box>
      </VStack>
    </Box>
  );
};

export default VehicleAssignmentColumn;
